/**
 * Company API Utilities
 * 
 * API endpoints for company user operations
 * Base URL follows existing pattern from MSP APIs
 */

const API_BASE = 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2';

/**
 * Get company configuration
 * Returns: { companyID, companyName, accounts: ["123456789012", ...], mspID }
 */
export const getCompanyConfig = async (companyID) => {
  const response = await fetch(`${API_BASE}/company/config?companyID=${companyID}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to get company configuration');
  }
  return response.json();
};

/**
 * Get company AWS accounts
 * Returns: { accounts: ["123456789012", ...] }
 */
export const getCompanyAccounts = async (companyID) => {
  const response = await fetch(`${API_BASE}/company/accounts?companyID=${companyID}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to get company accounts');
  }
  return response.json();
};

/**
 * Get company users (if user is admin)
 * Returns: { users: [{ email, accessLevel, status, invitedAt }, ...] }
 */
export const getCompanyUsers = async (companyID) => {
  const response = await fetch(`${API_BASE}/company/users?companyID=${companyID}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to get company users');
  }
  return response.json();
};

/**
 * Invite a new company user (admin only)
 * Body: { mspID, companyID, email, accessLevel: "admin" | "member" | "viewer" }
 */
export const inviteCompanyUser = async (mspID, companyID, email, accessLevel = 'member') => {
  if (!mspID) {
    throw new Error('mspID is required to invite company users');
  }
  
  const response = await fetch(`${API_BASE}/company/users/invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mspID, companyID, email, accessLevel })
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to invite company user');
  }
  return response.json();
};

/**
 * Delete a company user (admin only)
 * Query params: ?email=user@example.com&companyID=comp-uuid
 */
export const deleteCompanyUser = async (companyID, email) => {
  const response = await fetch(
    `${API_BASE}/company/users?email=${encodeURIComponent(email)}&companyID=${companyID}`,
    { method: 'DELETE' }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to delete company user');
  }
  return response.json();
};

/**
 * Company user login helper
 * Used after Cognito authentication to validate company access
 * Optional - can be used for additional authorization checks
 */
export const validateCompanyAccess = async (companyID, email) => {
  const response = await fetch(`${API_BASE}/company/users/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyID, email })
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Access denied');
  }
  return response.json();
};

export default {
  getCompanyConfig,
  getCompanyAccounts,
  getCompanyUsers,
  inviteCompanyUser,
  deleteCompanyUser,
  validateCompanyAccess
};
